import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import type { SwipeCallback, SwipeableHandlers } from "react-swipeable";
import { useSwipeable } from "react-swipeable";
import type { ProductFlatten, SelectedOption } from "~/types";
import { debounce } from "~/utils/useDebounce";
import Neum from "../neum.component";

const ProductCarousel: FunctionComponent<{
  product: ProductFlatten;
  selectedOptions: { [key: string]: string };
  setSelectedOptions: Dispatch<SetStateAction<{ [key: string]: string }>>;
  optionsQueue: string[];
  setOptionsQueue: Dispatch<SetStateAction<string[]>>;
}> = ({ product, selectedOptions, setSelectedOptions, optionsQueue, setOptionsQueue }) => {
  const scrollWidth = useRef<number>(0);
  const itemWidth = useRef<number>(0);
  const carousel = useRef<HTMLDivElement>(null);
  const carouselItem = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(2);
  const [currentItemImage, setCurrentItemImage] = useState(0);

  const getVariantsWithCurrentItemImage = (index: number) => {
    return product.variants.filter((variant) => variant.image?.url === product.images[index].url);
  };

  const addIfDoesNotExist = (arr: string[], element: string) => {
    if (!arr.includes(element)) {
      arr.push(element);
    }
  };

  const getOptionsWithCurrentItemImage = (index: number) => {
    const options = new Map<string, Set<string>>();
    const variants = getVariantsWithCurrentItemImage(index);
    for (const variant of variants) {
      for (const option of variant.selectedOptions) {
        const optionSet = options.get(option.name) ?? new Set<string>();
        optionSet.add(option.value);
        options.set(option.name, optionSet);
      }
    }

    if (options.size > 0) {
      const newOptions: { [key: string]: string } = { ...selectedOptions };
      let newQueue: string[] = [...optionsQueue];
      options.forEach((optionSet, name) => {
        if (optionSet.size === 1) {
          const [first] = optionSet;
          newOptions[name] = first;
          addIfDoesNotExist(newQueue, name);
        } else {
          if (optionSet.has(selectedOptions[name])) {
            newOptions[name] = selectedOptions[name];
            addIfDoesNotExist(newQueue, name);
          } else {
            newOptions[name] = "";
            // delete option from queue as well
            newQueue = optionsQueue.filter((option) => option !== name);
          }
        }
      });
      setSelectedOptions(newOptions);
      setOptionsQueue(newQueue);
    }
  };

  const updateIndex = (newIndex: number) => {
    let updatedIndex = newIndex;
    if (newIndex < 0) {
      updatedIndex = product.images.length - 1;
    } else if (newIndex >= product.images.length) {
      updatedIndex = 0;
    }
    getOptionsWithCurrentItemImage(updatedIndex);
    setActiveIndex(updatedIndex);
    setCurrentItemImage(updatedIndex);
  };

  const forward: SwipeCallback = () => {
    updateIndex(activeIndex + 1);
  };

  const backward: SwipeCallback = () => {
    updateIndex(activeIndex - 1);
  };

  useEffect(() => {
    const setWidth = () => {
      scrollWidth.current = carousel.current ? carousel.current.clientWidth : 0;
      itemWidth.current = carouselItem.current ? carouselItem.current.clientWidth : 0;
      setItemsPerSlide(Math.round(scrollWidth.current / itemWidth.current));
    };
    setWidth();
    const debouncedSetWidth = debounce(setWidth);
    window.addEventListener("resize", debouncedSetWidth);
  });

  const handlers: SwipeableHandlers = useSwipeable({
    onSwipedLeft: forward,
    onSwipedRight: backward,
  });

  const calculateTranslateX = () => {
    const maxLimit = product.images.length - itemsPerSlide;
    const position = activeIndex > maxLimit ? maxLimit : activeIndex;
    return position * itemWidth.current;
  };

  useEffect(() => {
    const atLeastOneOptionSelected = Object.values(selectedOptions).some((value) => !!value);
    const containsAllSelectedOptions = (
      variantSelectedOptions: SelectedOption[],
      selectedOptions: { [key: string]: string },
    ) => {
      return variantSelectedOptions.every((option) => {
        if (selectedOptions[option.name]) {
          return option.value === selectedOptions[option.name] ? true : false;
        }
        return true;
      });
    };
    if (atLeastOneOptionSelected) {
      // filter variants based on selected options queue
      const variant = product.variants.find((variant) =>
        containsAllSelectedOptions(variant.selectedOptions, selectedOptions),
      );
      // TODO: improve performance by creating a map of variants with images indexes
      const imgIndex = variant ? product.images.findIndex((image) => image.url === variant.image?.url) : -1;
      if (imgIndex >= 0) {
        setActiveIndex(imgIndex);
        setCurrentItemImage(imgIndex);
      }
    }
  }, [selectedOptions, product.variants, product.images]);

  const modal = useRef<HTMLDivElement>(null);
  const modalImg = useRef<HTMLImageElement>(null);
  function showModal(src: string) {
    modal.current?.classList.add("flex");
    modal.current?.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    if (modalImg.current) {
      modalImg.current.src = src;
    }
  }

  function closeModal() {
    document.getElementById("modalCarousel")?.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      updateIndex((currentItemImage + 1) % product.images.length);
    },
    onSwipedRight: () => {
      updateIndex((currentItemImage - 1 + product.images.length) % product.images.length);
    },
  });

  return (
    <div className="carousel mboverflow-hidden relative select-none">
      <Neum className="h-full w-full p-1">
        <div className="overflow-hidden" {...swipeHandlers}>
          <img
            id="productThumbnails"
            className="h-[307px] max-h-[307px] w-full rounded-lg bg-white object-contain  md:h-[669px] md:max-h-[669px] lg:h-[500px] lg:max-h-[500px]"
            width="500px"
            src={`${product.images[currentItemImage].url as string}&width=500`}
            alt={product.images[currentItemImage].altText ?? ""}
            onClick={() => {
              showModal(product.images[currentItemImage].url as string);
            }}
          />
        </div>
      </Neum>
      <div className="relative" id="productThumbnails">
        <ChevronLeftIcon
          className="absolute top-1/3 z-10 m-1 ml-0 h-6 w-6 flex-shrink-0 cursor-pointer rounded-full drop-shadow-md hover:text-admirerBlue sm:mt-2 sm:h-8 sm:w-8"
          aria-hidden="true"
          onClick={() => {
            updateIndex(activeIndex - 1);
          }}
        />
        <div {...handlers} className="relative mx-7 mt-2 overflow-hidden p-0 dark:p-0 sm:mx-12">
          <div
            ref={carousel}
            className="scroll-smooth whitespace-nowrap transition-transform"
            style={{ transform: `translateX(-${calculateTranslateX()}px)` }}
          >
            {product.images.map((productImg, index) => {
              return (
                <div
                  key={`${productImg.id}-${index}`}
                  ref={carouselItem}
                  className="box-border inline-flex w-1/3 snap-start items-center justify-center px-1 sm:w-1/3 lg:w-1/4"
                  onClick={() => {
                    updateIndex(index);
                  }}
                >
                  <Neum className="h-ful w-full p-1" active={currentItemImage === index}>
                    <div className="p-0">
                      <img
                        className="h-16 w-full rounded-lg object-cover sm:h-24 md:h-36 lg:h-24"
                        src={`${productImg.url as string}&width=100`}
                        alt={productImg.altText ?? ""}
                      />
                    </div>
                  </Neum>
                </div>
              );
            })}
          </div>
        </div>
        <ChevronRightIcon
          className="absolute right-0 top-1/3 z-10 m-1 mr-0 h-6 w-6 flex-shrink-0 cursor-pointer self-center rounded-full drop-shadow-md hover:text-admirerBlue sm:mt-2 sm:h-8 sm:w-8"
          aria-hidden="true"
          onClick={() => {
            updateIndex(activeIndex + 1);
          }}
        />
      </div>
      <div
        id="modalCarousel"
        ref={modal}
        className="fixed left-0 top-0 z-50 hidden h-screen w-screen items-center  justify-center bg-black/70"
      >
        <p
          className="z-90 fixed right-8 top-6 cursor-pointer text-5xl font-bold text-white "
          onClick={() => {
            closeModal();
          }}
        >
          &times;
        </p>

        <img
          ref={modalImg}
          id="modal-img"
          alt="product"
          src=""
          className="max-h-[600px] object-cover md:max-w-[800px]"
        />
      </div>
    </div>
  );
};

export default ProductCarousel;
